import { useCallback, useEffect, useState } from "react";

import { OutgoingIntakesAPI } from "../../../api/outgoingIntakesApi";
import type { NavBadgeKey } from "../navConfig";

export type AttentionBadges = {
  counts: Record<NavBadgeKey, number>;
  /** Total requests for the client; null until loaded. Drives onboarding. */
  intakesCount: number | null;
  isLoadingIntakesCount: boolean;
  refresh: () => void;
};

/**
 * Counts surfaced as nav badges: requests awaiting review. Re-read on
 * `refresh()` (sections call it after mutating) and whenever the client
 * changes.
 */
export const useClientAttentionBadges = (
  clientId: string | null
): AttentionBadges => {
  const [counts, setCounts] = useState<Record<NavBadgeKey, number>>({
    requests: 0
  });
  const [intakesCount, setIntakesCount] = useState<number | null>(null);
  const [isLoadingIntakesCount, setIsLoadingIntakesCount] = useState(false);
  const [nonce, setNonce] = useState(0);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    if (!clientId) {
      setCounts({ requests: 0 });
      setIntakesCount(null);
      setIsLoadingIntakesCount(false);
      return undefined;
    }

    setIsLoadingIntakesCount(true);
    OutgoingIntakesAPI.listForForeignNational(clientId)
      .then((intakes) => {
        if (cancelled) return;
        const list = Array.isArray(intakes) ? intakes : [];
        setIntakesCount(list.length);
        setCounts({
          requests: list.filter((i) => (i?.status ?? "") === "awaiting_review")
            .length
        });
      })
      .catch(() => {
        if (cancelled) return;
        setIntakesCount(0);
        setCounts({ requests: 0 });
      })
      .finally(() => {
        if (!cancelled) setIsLoadingIntakesCount(false);
      });

    return () => {
      cancelled = true;
    };
  }, [clientId, nonce]);

  return { counts, intakesCount, isLoadingIntakesCount, refresh };
};
